import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CreatePersonRequest, familyTreesAPI, peopleAPI } from '../services/api';
import { Button, IconButton, ModalFooter, SegmentedControl, formInputClassName, formSelectClassName } from './common';
import { PhotoUploadField } from './PhotoUploadField';
import { CropData } from './ImageCropModal';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { brandClasses } from '../theme';
import { isBirthDateAfterDeathDate, serializeDateInputToIso } from '../utils/personDateUtils';
import { trackEvent } from '../services/analytics';
import { useTranslation } from 'react-i18next';

interface AddPersonModalProps {
  treeId: string;
  onClose: () => void;
  onSuccess: (result: AddPersonSuccessResult) => void;
  showContinueToCompleteProfile?: boolean;
}

export interface AddPersonSuccessResult {
  newPersonId?: string;
  continueToCompleteProfile: boolean;
}

type SubmitMode = 'close' | 'continue';

const dateInputWrapperClassName = 'relative';
const inputClassName = formInputClassName;
const selectClassName = formSelectClassName;
const dateInputClassName = formInputClassName;
const clearDateButtonClassName =
  'absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-[11px] font-medium text-[#6B6560] transition hover:bg-[#EEF4FB] hover:text-[#185FA5]';

export const AddPersonModal: React.FC<AddPersonModalProps> = ({
  treeId,
  onClose,
  onSuccess,
  showContinueToCompleteProfile = true,
}) => {
  const { t } = useTranslation();
  const extractPayload = <T,>(payload: T | { data?: T | { data?: T } }): T => {
    if (typeof payload === 'object' && payload !== null && 'data' in payload) {
      const wrapped = payload as { data?: T | { data?: T } };
      return (typeof wrapped.data === 'object' && wrapped.data !== null && 'data' in wrapped.data
        ? (wrapped.data as { data?: T }).data
        : wrapped.data) as T;
    }

    return payload as T;
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    dateOfDeath: '',
    photoUrl: '',
    isDeceased: false,
    publicVisibility: 'Public' as 'Public' | 'Masked',
    allowPublicDisplayForLivingPerson: false,
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [cropData, setCropData] = useState<CropData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [livingVisibilityCapability, setLivingVisibilityCapability] = useState<{
    canEnableLivingPersonPublicDisplay: boolean;
    blockedReason?: string | null;
  } | null>(null);
  const pendingExitActionRef = useRef<(() => void) | null>(null);
  const displayName = [formData.firstName.trim(), formData.lastName.trim()].filter(Boolean).join(' ') || t('person.defaultName', { ns: 'canvas' });
  const hasUnsavedChanges = useMemo(
    () =>
      Boolean(
        formData.firstName.trim() ||
          formData.lastName.trim() ||
          formData.gender ||
          formData.dateOfBirth.trim() ||
          formData.dateOfDeath.trim() ||
          formData.photoUrl.trim() ||
          formData.isDeceased ||
          formData.publicVisibility !== 'Public' ||
          formData.allowPublicDisplayForLivingPerson ||
          photoFile ||
          cropData
      ),
    [cropData, formData, photoFile]
  );

  useEffect(() => {
    let isMounted = true;

    familyTreesAPI.getLivingPersonPublicDisplayCapability(treeId)
      .then((response) => {
        if (!isMounted) {
          return;
        }

        setLivingVisibilityCapability(response.data);
      })
      .catch(() => {
        if (isMounted) {
          setLivingVisibilityCapability(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [treeId]);

  const clearPendingExitAction = () => {
    pendingExitActionRef.current = null;
    setShowUnsavedChangesDialog(false);
  };

  const runPendingExitAction = () => {
    const pendingExitAction = pendingExitActionRef.current;
    clearPendingExitAction();
    pendingExitAction?.();
  };

  const requestExit = (action: () => void) => {
    if (loading) {
      return;
    }

    if (hasUnsavedChanges) {
      pendingExitActionRef.current = action;
      setShowUnsavedChangesDialog(true);
      return;
    }

    action();
  };

  const createPerson = async (mode: SubmitMode) => {
    setLoading(true);
    setError('');

    try {
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setError(t('validation.firstLastNameRequired', { ns: 'canvas' }));
        setLoading(false);
        return;
      }

      if (!formData.gender) {
        setError(t('validation.genderRequired', { ns: 'canvas' }));
        setLoading(false);
        return;
      }

      if (formData.isDeceased && isBirthDateAfterDeathDate(formData.dateOfBirth, formData.dateOfDeath)) {
        setError(t('validation.birthBeforeDeath', { ns: 'canvas' }));
        setLoading(false);
        return;
      }

      let dateOfBirth: string | undefined;
      if (formData.dateOfBirth && formData.dateOfBirth.trim()) {
        dateOfBirth = serializeDateInputToIso(formData.dateOfBirth);
      }

      let dateOfDeath: string | undefined;
      if (formData.isDeceased && formData.dateOfDeath && formData.dateOfDeath.trim()) {
        dateOfDeath = serializeDateInputToIso(formData.dateOfDeath);
      }

      const payload: CreatePersonRequest = {
        treeId,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        gender: formData.gender,
        publicVisibility: formData.publicVisibility,
        allowPublicDisplayForLivingPerson: formData.allowPublicDisplayForLivingPerson,
      };

      const isLiving = !formData.isDeceased;
      if (isLiving && formData.allowPublicDisplayForLivingPerson) {
        if (!livingVisibilityCapability?.canEnableLivingPersonPublicDisplay) {
          setError(livingVisibilityCapability?.blockedReason || t('person.livingPublicDisplayBlocked', { ns: 'canvas' }));
          setLoading(false);
          return;
        }

        const warningText = t('person.livingPublicDisplayConsentWarning', {
          ns: 'canvas',
          name: [formData.firstName.trim(), formData.lastName.trim()].filter(Boolean).join(' '),
        });

        if (!window.confirm(warningText)) {
          setLoading(false);
          return;
        }

        payload.livingPersonPublicDisplayConsent = {
          accepted: true,
          consentMessageVersion: 'living-public-display-consent-v1',
          warningTextSnapshot: warningText,
          consentSource: 'AddPersonModal',
          affectedPersonNamesSnapshot: [[formData.firstName.trim(), formData.lastName.trim()].filter(Boolean).join(' ')].filter(Boolean),
        };
      }

      if (dateOfBirth) {
        payload.dateOfBirth = dateOfBirth;
      }
      if (dateOfDeath) {
        payload.dateOfDeath = dateOfDeath;
      }

      const response = await peopleAPI.createPerson(payload);
      const personData = extractPayload<any>(response.data);
      const newPersonId = personData?.id;

      if (photoFile && newPersonId) {
        try {
          const photoFormData = new FormData();
          photoFormData.append('file', photoFile);

          if (cropData) {
            photoFormData.append('cropX', cropData.cropX.toString());
            photoFormData.append('cropY', cropData.cropY.toString());
            photoFormData.append('cropWidth', cropData.cropWidth.toString());
            photoFormData.append('cropHeight', cropData.cropHeight.toString());
            photoFormData.append('cropZoom', cropData.cropZoom.toString());
          }

          await peopleAPI.uploadPersonPhoto(newPersonId, photoFormData);
        } catch (photoUploadError) {
          console.error('AddPersonModal photo upload failed after person creation', {
            newPersonId,
            treeId,
            hasCropData: Boolean(cropData),
            fileName: photoFile.name,
            fileType: photoFile.type,
            fileSize: photoFile.size,
            error: photoUploadError,
          });
          // Do not fail person creation if photo upload is unsuccessful.
        }
      }

      trackEvent('person_added');
      onSuccess({
        newPersonId,
        continueToCompleteProfile: mode === 'continue',
      });
      runPendingExitAction();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail ||
                          err.response?.data?.message ||
                          err.message ||
                          t('person.failedToAdd', { ns: 'canvas' });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createPerson('close');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(26,23,20,0.45)] px-4 py-6"
      data-testid="add-person-backdrop"
      onClick={() => requestExit(onClose)}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] shadow-[0_24px_60px_rgba(26,23,20,0.24)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-person-modal"
      >
        <div className={`relative border-b px-5 py-4 pr-16 lg:pr-24 ${brandClasses.subtleBorder} ${brandClasses.titleBarSurface}`}>
          <IconButton
            type="button"
            onClick={() => requestExit(onClose)}
            className="absolute right-5 top-4"
            aria-label={t('person.closeQuickAddAriaLabel', { ns: 'canvas' })}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </IconButton>
          <div className="flex min-w-0 items-start gap-4">
            <div className="min-w-[104px]">
            <PhotoUploadField
              personId=""
              initialPhotoUrl={formData.photoUrl}
              onPhotoUrlChange={(url) => setFormData((current) => ({ ...current, photoUrl: url }))}
              onPhotoFileChange={(file) => setPhotoFile(file)}
              onCropDataChange={(data) => setCropData(data)}
              disabled={loading}
              compact={true}
              hideUrlField={true}
              allowUrlUpload={false}
              uploadLabel="Upload"
            />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <p className="font-['Playfair_Display',Georgia,serif] text-[30px] font-semibold text-[#1A1714]">
                    {displayName}
                  </p>
                  <div className="flex justify-end pr-4 lg:pr-8">
                    <Button
                      type="submit"
                      form="add-person-form"
                      size="lg"
                      disabled={loading}
                      data-testid="person-save-button"
                      className="min-w-[118px] rounded-[18px]"
                    >
                      {loading ? t('status.saving', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}
                    </Button>
                  </div>
                </div>
                <p className="mt-1 text-sm text-[#6B6560]">
                  {showContinueToCompleteProfile
                    ? t('person.essentialsHint', { ns: 'canvas' })
                    : t('person.createRelatedHint', { ns: 'canvas' })}
                </p>
                <div className="mt-3">
                  <SegmentedControl
                    value={formData.isDeceased ? 'deceased' : 'living'}
                    onChange={(value) =>
                      setFormData((current) => ({
                        ...current,
                        isDeceased: value === 'deceased',
                        dateOfDeath: value === 'living' ? '' : current.dateOfDeath,
                        allowPublicDisplayForLivingPerson: value === 'deceased' ? false : current.allowPublicDisplayForLivingPerson,
                      }))
                    }
                    items={[
                      { value: 'living', label: t('person.status.living', { ns: 'canvas' }) },
                      { value: 'deceased', label: t('person.status.deceased', { ns: 'canvas' }) },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <form id="add-person-form" onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-5 py-5">
            {error && (
              <div className="rounded-xl border border-[#F7C1C1] bg-[#FCEBEB] px-4 py-3 text-sm text-[#A32D2D]">
                {error}
              </div>
            )}

            <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.85fr)]">
              <div className="space-y-5">
                <section className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] p-5 shadow-[0_10px_30px_rgba(26,23,20,0.05)]">
                  <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#185FA5]">{t('person.sections.identity', { ns: 'canvas' })}</div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#6B6560]">
                    {t('person.fields.firstName', { ns: 'canvas' })} <span className="text-[#A32D2D]">*</span>
                  </span>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={formData.firstName}
                    onChange={(e) => setFormData((current) => ({ ...current, firstName: e.target.value }))}
                    placeholder={t('person.fields.firstNamePlaceholder', { ns: 'canvas' })}
                    data-testid="person-first-name-input"
                    className={inputClassName}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#6B6560]">
                    {t('person.fields.lastName', { ns: 'canvas' })} <span className="text-[#A32D2D]">*</span>
                  </span>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData((current) => ({ ...current, lastName: e.target.value }))}
                    placeholder={t('person.fields.lastNamePlaceholder', { ns: 'canvas' })}
                    data-testid="person-last-name-input"
                    className={inputClassName}
                  />
                </label>
                  </div>
                  <label className="mt-4 flex flex-col gap-1">
                    <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#6B6560]">
                      {t('person.fields.publicVisibility', { ns: 'canvas' })}
                    </span>
                    <select
                      value={formData.publicVisibility}
                      onChange={(e) =>
                        setFormData((current) => ({
                          ...current,
                          publicVisibility: e.target.value as 'Public' | 'Masked',
                          allowPublicDisplayForLivingPerson:
                            e.target.value === 'Masked' ? false : current.allowPublicDisplayForLivingPerson,
                        }))
                      }
                      aria-label={t('person.fields.publicVisibility', { ns: 'canvas' })}
                      className={selectClassName}
                    >
                      <option value="Public">{t('person.publicVisibility.public', { ns: 'canvas' })}</option>
                      <option value="Masked">{t('person.publicVisibility.masked', { ns: 'canvas' })}</option>
                    </select>
                  </label>
                  <div className="mt-3 rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-3 text-sm text-[#6B6560]">
                    <p>{t('person.publicVisibilityHint', { ns: 'canvas' })}</p>
                    {!formData.isDeceased ? (
                      <p className="mt-2 font-medium text-[#7A3E1D]">{t('person.livingVisibilityWarning', { ns: 'canvas' })}</p>
                    ) : null}
                  </div>
                  {!formData.isDeceased && formData.publicVisibility === 'Public' ? (
                    <label className="mt-4 flex items-start gap-3 rounded-2xl border border-[#DCC9A3] bg-[#FFF8E8] px-4 py-3 text-sm text-[#5D4A1F]">
                      <input
                        type="checkbox"
                        checked={formData.allowPublicDisplayForLivingPerson}
                        onChange={(e) =>
                          setFormData((current) => ({
                            ...current,
                            allowPublicDisplayForLivingPerson: e.target.checked,
                          }))
                        }
                        disabled={!livingVisibilityCapability?.canEnableLivingPersonPublicDisplay}
                        className="mt-0.5 h-4 w-4 rounded border-[#B89C5B]"
                      />
                      <span>
                        <span className="block font-medium">{t('person.livingPublicDisplayToggle', { ns: 'canvas' })}</span>
                        <span className="mt-1 block text-xs text-[#6B6560]">
                          {livingVisibilityCapability?.canEnableLivingPersonPublicDisplay
                            ? t('person.livingPublicDisplayHelp', { ns: 'canvas' })
                            : livingVisibilityCapability?.blockedReason || t('person.livingPublicDisplayBlocked', { ns: 'canvas' })}
                        </span>
                      </span>
                    </label>
                  ) : null}
                </section>

                <section className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] p-5 shadow-[0_10px_30px_rgba(26,23,20,0.05)]">
                  <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#185FA5]">{t('person.sections.vitalDetails', { ns: 'canvas' })}</div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#6B6560]">
                    {t('person.fields.gender', { ns: 'canvas' })} <span className="text-[#A32D2D]">*</span>
                  </span>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData((current) => ({ ...current, gender: e.target.value }))}
                    aria-label={t('person.fields.gender', { ns: 'canvas' })}
                    className={selectClassName}
                  >
                    <option value="" disabled>
                      {t('person.fields.genderPlaceholder', { ns: 'canvas' })}
                    </option>
                    <option value="Unspecified">{t('person.gender.unspecified', { ns: 'canvas' })}</option>
                    <option value="Male">{t('person.gender.male', { ns: 'canvas' })}</option>
                    <option value="Female">{t('person.gender.female', { ns: 'canvas' })}</option>
                    <option value="Other">{t('person.gender.other', { ns: 'canvas' })}</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#6B6560]">{t('person.fields.dateOfBirth', { ns: 'canvas' })}</span>
                  <div className={dateInputWrapperClassName}>
                    <input
                      type="date"
                      aria-label={t('person.fields.dateOfBirth', { ns: 'canvas' })}
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData((current) => ({ ...current, dateOfBirth: e.target.value }))}
                      className={`${dateInputClassName} ${formData.dateOfBirth ? 'pr-16' : ''}`}
                    />
                    {formData.dateOfBirth && (
                      <button
                        type="button"
                        onClick={() => setFormData((current) => ({ ...current, dateOfBirth: '' }))}
                        className={clearDateButtonClassName}
                        aria-label={t('person.fields.clearDateOfBirth', { ns: 'canvas' })}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#6B6560]">{t('person.fields.dateOfDeath', { ns: 'canvas' })}</span>
                  <div className={dateInputWrapperClassName}>
                    <input
                      type="date"
                      aria-label={t('person.fields.dateOfDeath', { ns: 'canvas' })}
                      value={formData.dateOfDeath}
                      onChange={(e) => setFormData((current) => ({ ...current, dateOfDeath: e.target.value }))}
                      disabled={!formData.isDeceased}
                      className={`${dateInputClassName} ${formData.dateOfDeath ? 'pr-16' : ''}`}
                    />
                    {formData.isDeceased && formData.dateOfDeath && (
                      <button
                        type="button"
                        onClick={() => setFormData((current) => ({ ...current, dateOfDeath: '' }))}
                        className={clearDateButtonClassName}
                        aria-label={t('person.fields.clearDateOfDeath', { ns: 'canvas' })}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </label>
                  </div>
                </section>
              </div>

              <div className="space-y-5">
                <section className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] p-5 shadow-[0_10px_30px_rgba(26,23,20,0.05)]">
                  <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#185FA5]">{t('person.sections.nextStep', { ns: 'canvas' })}</div>
                  <div className="mt-4 rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-4 text-sm text-[#6B6560]">
                    <p className="font-medium text-[#1A1714]">{t('person.sections.whatHappensNext', { ns: 'canvas' })}</p>
                    <p className="mt-1">
                      {showContinueToCompleteProfile
                        ? 'Save and close to return to the canvas, or continue into the full profile to add address, family links, biography, and other details.'
                        : 'Save now and the selected relationship will be created automatically.'}
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>

          <ModalFooter className="px-5">
            <Button
              type="button"
              variant="outline"
              onClick={() => requestExit(onClose)}
              className="sm:min-w-[160px]"
            >
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            {showContinueToCompleteProfile && (
              <Button
                type="button"
                disabled={loading}
                onClick={() => void createPerson('continue')}
                className="sm:min-w-[240px]"
              >
                {loading ? t('status.saving', { ns: 'common' }) : t('person.continueToCompleteProfile', { ns: 'canvas' })}
              </Button>
            )}
          </ModalFooter>
        </form>
      </div>

      <UnsavedChangesDialog
        isOpen={showUnsavedChangesDialog}
        title={t('person.unsavedDialog.title', { ns: 'canvas' })}
        message={t('person.unsavedDialog.message', { ns: 'canvas' })}
        onSave={() => {
          void createPerson('close');
        }}
        onDiscard={runPendingExitAction}
        onCancel={clearPendingExitAction}
        saving={loading}
      />
    </div>
  );
};
